import { ButtonHTMLAttributes, ReactElement } from "react";
import { useModal } from "@/hooks";

import styles from "./GenericDialog.module.css";

type ButtonConfig = "OK" | "OKCancel" | "YesNoCancel";

type GenericDialogProps = {
  caption: string;
  buttons?: ButtonConfig;
  children: ReactElement | ReactElement[] | string;
  className?: string;
  onAccept?: () => void | boolean | Promise<void | boolean>;
  onCancel?: () => void;
  allowAccept?: boolean;
  allowCancel?: boolean;
};

const defaultProps: GenericDialogProps = {
  caption: "",
  children: "",
  buttons: "OK",
  allowAccept: true,
  allowCancel: true,
};

const Button = (props: ButtonHTMLAttributes<HTMLButtonElement> & { caption: string }) => (
  <button type="button" className="btn btn-default me-1" {...props}>
    {props.caption}
  </button>
);

export const GenericDialog = (props: GenericDialogProps) => {
  const { caption, buttons, children, className, allowAccept, allowCancel } = { ...defaultProps, ...props };

  const modal = useModal({
    onCloseRequested: handleCancel,
  });

  async function handleAccept() {
    let shouldClose = true;

    if (props.onAccept) {
      const result = props.onAccept();
      const resolved = result instanceof Promise ? await result : result;
      if (resolved === false) {
        shouldClose = false;
      }
    }

    if (shouldClose) {
      modal.close();
    }
  }

  function handleCancel() {
    if (props.onCancel) {
      props.onCancel();
    }
    modal.close();
  }

  return (
    <div className={className}>
      <h5 className={styles.header}>{caption}</h5>
      <div className={styles.body}>{children}</div>
      <div className={styles.footer}>
        {buttons === "OK" && (
          <>
            <Button onClick={handleAccept} caption="OK" disabled={!allowCancel} />
          </>
        )}
        {buttons === "OKCancel" && (
          <>
            <Button onClick={handleAccept} caption="OK" disabled={!allowAccept} />
            <Button onClick={handleCancel} caption="Cancel" disabled={!allowCancel} />
          </>
        )}
        {buttons === "YesNoCancel" && (
          <>
            <Button onClick={handleAccept} caption="Yes" />
            <Button onClick={handleCancel} caption="No" />
            <Button onClick={handleCancel} caption="Cancel" disabled={!allowCancel} />
          </>
        )}
      </div>
    </div>
  );
};
